import { FormattedMessage } from 'react-intl';

import { useMemberId } from '@trello/authentication';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { Popover, usePopover } from '@trello/nachos/popover';

import { WorkspaceBoardsList } from './WorkspaceBoardsList';
import { useWorkspaceBoardsOrganizationFragment } from './WorkspaceBoardsOrganizationFragment.generated';
import { useWorkspaceCollaboratorItemFragment } from './WorkspaceCollaboratorItemFragment.generated';
import { useWorkspaceMemberItemFragment } from './WorkspaceMemberItemFragment.generated';

import styles from './WorkspaceBoards.less';

interface WorkspaceBoardsProps {
  idMember: string;
  idOrganization: string;
}

export function WorkspaceBoards({
  idMember,
  idOrganization,
}: WorkspaceBoardsProps) {
  const currentMemberId = useMemberId();

  const { data: organization } = useWorkspaceBoardsOrganizationFragment({
    from: { id: idOrganization },
    optimistic: true,
  });

  const { data: collaborator } = useWorkspaceCollaboratorItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const { data: member } = useWorkspaceMemberItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const isCurrentMemberAdmin =
    organization?.memberships?.find(
      (membership) => membership.idMember === currentMemberId,
    )?.memberType === 'admin';

  const { triggerRef, toggle, popoverProps, hide } = usePopover();

  if (!isCurrentMemberAdmin) {
    return null;
  }

  const collaboratorBoardCount = collaborator?.boardCount;
  const memberBoardCount =
    member?.id &&
    organization?.boardCounts?.find(
      (boardCountObj) => boardCountObj.idMember === idMember,
    )?.boardCount;

  return (
    <>
      <Button
        className={styles.WorkspaceBoardsButton}
        isDisabled={!collaboratorBoardCount && !memberBoardCount}
        onClick={toggle}
        ref={triggerRef}
      >
        <FormattedMessage
          id="templates.org_member_activity.view-boards"
          description="Title of button which includes the number of boards the Workspace member is a member of"
          defaultMessage="View boards ({boardCount})"
          values={{
            boardCount: memberBoardCount || collaboratorBoardCount || 0,
          }}
        />
      </Button>
      <Popover
        {...popoverProps}
        title={intl.formatMessage({
          id: 'templates.org_member_activity.popover-title',
          defaultMessage: 'Workspace boards',
          description:
            "Title of a popover showing a list of a user's boards in the current Workspace",
        })}
      >
        <WorkspaceBoardsList
          idMember={idMember}
          idOrganization={idOrganization}
          fullName={member?.fullName || collaborator?.fullName}
          boardCount={memberBoardCount || collaboratorBoardCount || 0}
          hidePopover={hide}
        />
      </Popover>
    </>
  );
}
